// frontend/src/pages/PaychequeDetail/index.tsx
// Thin orchestrator: state, data wiring, auto-print, modal coordination.
// Delegates heavy JSX to PayStub + sub-components, mutations to useMutations.

import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { HelpCircle, X, BookOpen, Printer } from "lucide-react";

import PayStub from "../../components/payroll/PayStub";
import CreateAdjustmentModal from "../../components/payroll/CreateAdjustmentModal";
import AdjustmentGuardModal from "../../components/payroll/AdjustmentGuardModal";
import VoidPaychequeModal from "../../components/payroll/VoidPaychequeModal";
import DeleteGuardModal from "../../components/payroll/DeleteGuardModal";

import type { PaychequeDetail } from "./types";
import { buildDocTitle, buildPrintTitle, isPrintMode } from "./helpers";
import {
  BG_CARD,
  BORDER,
  BRAND,
  TEXT_SECONDARY,
  outlineBtn,
  brandOutlineBtn,
  primaryBtn,
} from "./styles";
import { usePaychequeDetail } from "./usePaychequeDetail";
import { usePaychequeDetailMutations } from "./useMutations";

import LoadingState from "../../components/common/LoadingState";

import MemoEditor from "./components/MemoEditor";
import AdjustmentBanner from "./components/AdjustmentBanner";
import ActionsMenu from "./components/ActionsMenu";

// Re-export the detail type so other files can import it from here
export type { PaychequeDetail } from "./types";

export default function PaychequeDetail(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  // ── State ───────────────────────────────────────────────────────────────
  const [pc, setPc] = useState<PaychequeDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [memo, setMemo] = useState<string>("");

  // Modal open state
  const [adjustOpenModal, setAdjustOpenModal] = useState<boolean>(false);
  const [guardOpenModal, setGuardOpenModal] = useState<boolean>(false);
  const [voidOpen, setVoidOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  // ── Mutations ───────────────────────────────────────────────────────────
  const {
    saveMemo: saveMemoMutation,
    voidPaycheque: voidPaychequeMutation,
  } = usePaychequeDetailMutations();

  // ── Data ────────────────────────────────────────────────────────────────
  const { data: paychequeData, isLoading: qLoading, error: qError } = usePaychequeDetail(id);

  useEffect(function () {
    if (paychequeData) {
      setPc(paychequeData);
      setMemo(paychequeData.memo || "");
      try {
        document.title = buildDocTitle(paychequeData);
      } catch {
        // Ignore title-set failures (SSR, headless, etc.)
      }
    }
    setLoading(qLoading);
    if (qError) setError((qError as Error).message);
  }, [paychequeData, qLoading, qError]);

  // Not currently called from the UI, but kept so modals can trigger a refresh if needed later.
  function refetch() {
    queryClient.invalidateQueries({ queryKey: ["paycheque-detail", id] });
  }

  // ── Auto-print when ?print=1 URL param is set ───────────────────────────
  useEffect(function () {
    if (!pc || !isPrintMode()) return;
    document.title = buildPrintTitle(pc);
    const timer = setTimeout(function () { window.print(); }, 100);
    return function () { clearTimeout(timer); };
  }, [pc]);

  // ── Side-effect wrappers ────────────────────────────────────────────────
  async function handleSaveMemo(): Promise<void> {
    if (!pc) return;
    if (memo === (pc.memo || "")) return;
    const result = await saveMemoMutation(pc.id, memo);
    if (result.success) {
      setPc({ ...pc, memo });
    } else {
      console.warn("Could not save memo:", result.error);
    }
  }

  async function handleVoid(reason: string): Promise<void> {
    if (!pc) return;
    const result = await voidPaychequeMutation(pc.id, reason);
    if (!result.success) {
      throw new Error(result.error || "Could not void paycheque");
    }
    setPc({ ...pc, status: "voided" });
  }

  function handleEdit(): void {
    if (pc && pc.is_adjustment) {
      setGuardOpenModal(true);
    } else {
      setAdjustOpenModal(true);
    }
  }

  function close(): void {
    // Opened via link in-app → go back. Opened in a new tab → try to close the tab,
    // then fall back to the paycheques list if the browser blocks window.close().
    if (window.history.length > 1 && document.referrer) {
      navigate(-1);
    } else {
      window.close();
      setTimeout(function () { navigate("/payroll/paycheques"); }, 100);
    }
  }

  // ── Early returns ───────────────────────────────────────────────────────
  if (loading) {
    return <LoadingState message="Loading..." padding={40} fontSize={14} color={TEXT_SECONDARY} />;
  }

  if (error || !pc) {
    return (
      <div style={{ padding: 40, fontFamily: "inherit", textAlign: "center" }}>
        <div style={{ fontSize: 14, color: "#991B1B", marginBottom: 12 }}>
          {error || "Paycheque not available."}
        </div>
        <button
          onClick={close}
          style={{
            fontSize: 12,
            padding: "8px 16px",
            borderRadius: 5,
            background: BRAND,
            color: "white",
            border: "none",
            cursor: "pointer",
            fontWeight: 500,
            fontFamily: "inherit",
          }}
        >
          Back to list
        </button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      className="paycheque-print-area"
      style={{
        background: BG_CARD,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "inherit",
      }}
    >
      <style>{`
        @page {
          margin: 12mm 10mm;
          size: A4;
        }
        @media print {
          [data-print="hide"], [data-print='hide'], .no-print { display: none !important; }
          html, body { background: white !important; }
          body * { visibility: hidden; }
          .paystub-container, .paystub-container * { visibility: visible; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .paystub-container { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; display: block !important; }
        }
        @media screen {
          .pay-stub-print-only { display: none; }
        }
      `}</style>

      {/* Pay stub (always visible, print target) */}
      <div
        className="paystub-container"
        style={{ padding: "24px", background: "#F4F6F8", display: "flex", justifyContent: "center" }}
      >
        <PayStub data={pc} />
      </div>

      {/* Top-right actions (not printed) */}
      <div
        className="no-print"
        style={{
          padding: "12px 20px",
          borderBottom: "0.5px solid #F3F4F6",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
        }}
      >
        <ActionsMenu
          onEdit={handleEdit}
          onVoid={() => setVoidOpen(true)}
          onDelete={() => setDeleteOpen(true)}
        />
        <HelpCircle
          size={18}
          style={{ color: TEXT_SECONDARY, cursor: "pointer" }}
          onClick={() => alert("Help coming soon")}
        />
        <X
          size={18}
          style={{ color: TEXT_SECONDARY, cursor: "pointer" }}
          onClick={close}
        />
      </div>

      {/* Memo + adjustment banner (not printed) */}
      <div
        className="no-print"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px 100px",
          maxWidth: 880,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {pc.is_adjustment && <AdjustmentBanner reason={pc.adjustment_reason} />}
        <MemoEditor value={memo} onChange={setMemo} onBlur={handleSaveMemo} />
      </div>

      {/* Fixed footer (not printed) */}
      <div
        className="no-print"
        style={{
          padding: "12px 24px",
          borderTop: "0.5px solid " + BORDER,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          background: BG_CARD,
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
          zIndex: 100,
        }}
      >
        <button onClick={close} style={outlineBtn}>Close</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => alert("Transaction journal coming soon")}
            style={brandOutlineBtn}
          >
            <BookOpen size={13} />
            Transaction journal
          </button>
          <button onClick={() => window.print()} style={primaryBtn}>
            <Printer size={13} />
            Print pay stub
          </button>
        </div>
      </div>

      {/* Modals */}
      <CreateAdjustmentModal
        open={adjustOpenModal}
        onClose={() => setAdjustOpenModal(false)}
        originalStub={pc}
        onCreated={() => { window.location.reload(); }}
      />
      <AdjustmentGuardModal
        open={guardOpenModal}
        onClose={() => setGuardOpenModal(false)}
        stub={pc}
        onVoid={() => setVoidOpen(true)}
      />
      <VoidPaychequeModal
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        paycheque={pc as any}
        onConfirm={handleVoid}
      />
      <DeleteGuardModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        stub={pc}
        onVoid={() => setVoidOpen(true)}
      />
    </div>
  );
}